import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { company, serviceFaqs } from '../mock';
import { FaqAccordion } from '../sections/RestSections';

import Seo from '../components/Seo';
import StructuredData from '../components/StructuredData';

const services = [
  {
    title: 'Kitchen & Bath Fixture Installation & Repair',
    img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/j1ofpqsg_07%3A18%3A26.6.webp',
    body: 'We source only the highest-quality components and focus on aesthetic appeal, functionality, and personalization when helping you make your selection. With keen attention to detail and an emphasis on customer satisfaction, Victory Plumbing is a reliable choice for fixture installation and repair in Savannah.',
  },
  {
    title: 'Water Heater Repair & Installation',
    img: '/img/water-heater.jpg',
    landing: '/residential/water-heater-repair-savannah',
    body: 'We install and service both traditional tank and tankless water heaters. A properly functioning heater does more than deliver warm water \u2014 it helps maintain the sanitation of your entire plumbing system. Reach out for an evaluation and estimate.',
  },
  {
    title: 'Water, Gas & Sewer Line Service',
    img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/j0lckjy8_07%3A18%3A26.2.jpg',
    landing: '/residential/sewer-line-repair-savannah',
    body: 'Our seasoned team offers expert repair, replacement, and installation for all of your water, gas, and sewer line needs. When it comes to anything line-related, we deliver fast, reliable, and efficient service at competitive prices across Savannah and the surrounding areas.',
  },
  {
    title: 'Clog Removal',
    img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/nvdmqgmp_07%3A21%3A26.webp',
    landing: '/residential/drain-cleaning-savannah',
    body: 'A clogged drain is a serious nuisance. Our team provides quick, efficient, and cost-effective clog removal, working around your schedule and walking you through the issue so we\u2019re always on the same page.',
    signs: [
      'Water backing up in the sink',
      'Pools of water forming near drains',
      'Water draining very slowly',
      'Toilet water bubbling when the sink runs',
      'Gurgling drains after the washing machine or dishwasher',
      'A rotting-food smell coming from the drain',
    ],
  },
  {
    title: 'Slab Leak Detection & Repair',
    img: '/img/pool-manifold.jpg',
    landing: '/residential/slab-leak-detection-savannah',
    body: 'A leak beneath your concrete foundation can quietly damage your home and inflate your water bill. We use electronic and acoustic detection to locate slab leaks fast, then repair, reroute, or repipe with minimal disruption \u2014 and honest, upfront pricing.',
  },
  {
    title: 'Water Filtration & Softener Systems',
    img: '/img/pool-manifold.jpg',
    landing: '/residential/water-filtration-savannah',
    body: 'Hard water and poor water quality are tough on your pipes, appliances, and skin. We install and service whole-home water filtration and softener systems for cleaner, better-tasting, softer water \u2014 helping you pick the right system and installing it right the first time.',
  },
  {
    title: 'Same-Day Plumbing Repair',
    img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/nvdmqgmp_07%3A21%3A26.webp',
    landing: '/residential/same-day-plumbing-repair-savannah',
    body: 'When a plumbing problem can\u2019t wait, we\u2019re ready. In most cases we can get to you the same day you call to fix leaks, clogs, water heater failures, and running toilets \u2014 on time, with a straightforward estimate before we start.',
  },
];

export default function ResidentialPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="page services-page">
      <Seo title="Residential Plumbing in Savannah" description="Home plumbing in Savannah, GA: fixture installation, water heaters, water/gas/sewer lines, clog & drain removal, remodeling & repiping." path="/residential" />
      <StructuredData faqItems={serviceFaqs} />
      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">Residential</p>
          <h1>Comprehensive plumbing care for your home</h1>
          <p className="lede">
            Reliable, detail-driven service for everyday repairs and major upgrades alike &mdash; delivered
            with the craftsmanship and customer care Savannah families count on.
          </p>
        </div>
      </section>

      <section className="about-values">
        <div className="wrap">
          <div className="pos-grid svc-detail-grid">
            {services.map((s) => (
              <div className="pos-card svc-photo-card" key={s.title}>
                <Link to="/gallery" className="svc-photo" data-testid="residential-photo-link" aria-label="View our gallery">
                  <img src={s.img} alt={s.title} loading="lazy" />
                </Link>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                {s.signs && (
                  <>
                    <p className="sign-intro">Signs you may need clog removal include:</p>
                    <ul className="sign-list">
                      {s.signs.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                  </>
                )}
                {s.landing && (
                  <Link className="svc-learn" to={s.landing}>Learn more <ArrowRight size={14} /></Link>
                )}
              </div>
            ))}
          </div>
          <div className="work-actions">
            <Link className="btn btn-gold" to="/gallery" data-testid="residential-view-gallery">View Full Gallery <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="about-body">
        <div className="wrap">
          <div className="about-craft">
            <p className="eyebrow" style={{ justifyContent: 'center' }}>Remodeling &amp; Repiping</p>
            <h3>Renovations guided by real expertise</h3>
            <p>
              Whether you&rsquo;re updating a single bathroom or repiping an entire historic home, our specialists
              handle relocating lines, repiping, and fixture installation &mdash; advising on what&rsquo;s possible,
              safe, and code-compliant, with added experience in Savannah&rsquo;s historic structures.
            </p>
            <div className="about-cta" style={{ justifyContent: 'center' }}>
              <Link className="btn btn-gold" to="/contact"><Mail size={16} /> Get in Touch</Link>
              <a className="btn btn-outline-dark" href={company.phoneHref}><Phone size={15} /> {company.phoneDisplay}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-full" data-testid="residential-faqs">
        <div className="wrap">
          <div className="sec-head" style={{ marginBottom: '28px' }}>
            <p className="eyebrow">Common Questions</p>
            <h2>Service plumbing FAQs</h2>
            <p>Answers about our services, pricing, licensing, and coverage across Savannah and the Coastal Empire.</p>
          </div>
          <FaqAccordion items={serviceFaqs.slice(0, 4)} />
          <div className="faq-actions">
            <Link className="btn btn-outline-dark" to="/faq" data-testid="residential-view-all-faqs">View All FAQs</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
